<template>
  <div class="embedded-recording-registration">
    <img v-if="bgImg" class="imClass" :src="bgImg" @error="replaceWithDefault" />
    <template v-if="view === 'PlayButton'">
      <div class="preview-display" v-if="isFormVisible">
        <div class="preview-inner-display">
          <a tabindex="1" class="play-link" @click="clickInitial()"><icon kind="play"></icon></a>
        </div>
      </div>
    </template>
    <template v-else>
      <div class="preview" v-if="isFormVisible">
        <div class="preview-inner">
          <icon kind="play"></icon>
        </div>
      </div>
      <div class="form-wrapper" v-if="isFormVisible">
        <b-row align-h="center" align-v="center">
          <div class="spin" v-if="registering">
            <spinner size="1x"></spinner>
          </div>
          <b-col md="10" class="text-center">
            <span class="form-header">
                {{ $t('registration.form.header') }}
            </span>
            <registration-form :fields="fields" @submit="register" :disclaimer="$t('registration.form.emailDisclaimer')">
              <p slot="disclaimer" class="registration-disclaimer">
                {{ $t('registration.form.emailDisclaimer') }}
              </p>

            </registration-form>
          </b-col>
        </b-row>
      </div>
      <div class="spin" v-else>
        <spinner size="1x"></spinner>
      </div>
    </template>

    <captcha
      v-if="verifyHumanRegistrant"
      @onCaptchaToken="onCaptchaToken"
      ref="captcha"
    ></captcha>
  </div>
</template>

<script>
  import Spinner from '@/components/togo/spinner';
  import Icon from '@/components/togo/icon';
  import RegistrationForm from '../../registration/registration-form';
  import { createRenditionUrl } from '@/helpers/image-rendition-helper';
  import windowUtils from '@/utils/window-utils';
  import Captcha from '@/components/captcha';

  const EmbeddedRecordingRegistration = {
    name: 'EmbeddedRecordingRegistration',
    props: ['embeddedResourceId'],
    async created() {
      await this.$store.dispatch('g2sRecording/initialize', { embeddedResourceId: this.embeddedResourceId });
      this.disclaimerText = await this.$store.dispatch('g2sRecording/getDisclaimer');
    },
    components: {
      RegistrationForm,
      Spinner,
      Icon,
      Captcha
    },
    data() {
      return {
        registering: false,
        initialClicked: false,
        fields: [
          {field: 'firstName'},
          {field: 'lastName'},
          {field: 'email'}
        ],
        formFields: null,
        disclaimerText: null
      };
    },

    computed: {
      embeddedResource() {
        return this.$store.state.g2sRecording.embeddedResource;
      },

      thumbnailLocation() {
        const thumbnail = this.embeddedResource && (this.embeddedResource.thumbnail || this.embeddedResource.defaultThumbnail);
        return thumbnail && (thumbnail.cdnLocation || thumbnail.location);
      },

      isFormVisible() {
        return this.embeddedResource;
      },

      bgImg() {
        const defaultUrl = this.thumbnailLocation;
        if(defaultUrl) {
          return createRenditionUrl(defaultUrl);
        }
        return '';
      },

      view() {
        return !this.initialClicked ? 'PlayButton' : '';
      },

      verifyHumanRegistrant() {
        return this.$store.state.g2sRecording.verifyHumanRegistrant && !this.disclaimerText;
      }
    },

    watch: {
      isFormVisible(newValue) {
        if (newValue) {
          this.$telemetryTrack('Embedded Landing Page Displayed', {embeddedResource: this.embeddedResource});
        }
      }
    },

    methods: {
      clickInitial() {
        this.initialClicked = true;
        this.$telemetryTrack('Embedded Play Button Clicked');
      },

      replaceWithDefault(e) {
        e.target.src = this.thumbnailLocation;
      },

      async register(formData) {
        if (this.verifyHumanRegistrant && !formData.humanRegistrantToken) {
          this.formFields = formData;
          this.$refs.captcha.execute();
          return;
        }

        this.registering = true;

        this.$telemetryTrack('Embedded Register Button Clicked', {
          ...formData,
          ...this.$store.state.g2sRecording.embeddedResource
        });

        delete formData['responses'];

        const source = windowUtils.readQueryParameter('source', this.$route);
        if(source) {
          this.$store.commit('recording/setSource', {source});
        }

        if (this.disclaimerText) {
          this.$router.push({name: 'disclaimer', params: {
              formData,
              disclaimerText: this.disclaimerText,
              verifyHumanRegistrant: this.$store.state.g2sRecording.verifyHumanRegistrant
          }});
        } else {
          const {registrantKey, recordingKey} = await this.$store.dispatch('g2sRecording/registerAndGetAssets', {
            formData
          });

          this.$router.push({
            name: 'embedded-recording-view',
            params: {
              recordingKey,
              registrantKey,
              controlsPlayback: true,
            }
          });
          this.$telemetryTrack('Embedded registration complete', {
            ...formData,
            ...this.$store.state.g2sRecording.embeddedResource});
        }
      },

      onCaptchaToken(token) {
        this.formFields.humanRegistrantToken = token;
        this.formFields.humanVerificationEngine = 'recaptcha';
        this.register(this.formFields);
      }
    }
  }

  export default EmbeddedRecordingRegistration;
</script>

<style lang="sass">
  @import 'src/scss/variables'
  $max-height-thumbnail: 500px
  $max-width-thumbnail: 500px
  $min-embed-width: 640px

  input[type="text"]
    background: transparent
    border: 1px solid $white
    color: $white
    font-weight: 300
  input[type="text"]::placeholder
    color: $white
    font-weight: 300

  .validationErrorMessage
    color: $white !important

  .embedded-recording-registration
    height: 100%
    width: 100%
    display: flex
    flex-direction: column
    overflow: hidden

    .imClass
      position: absolute
      z-index: -1
      width: 100%
      height: 100%
      object-fit: cover

    .spin
      display: flex
      align-items: center
      justify-content: center
      font-size: 6em
      position: absolute
      left: 0
      right: 0

    .play-link
      cursor: pointer
      color: $white
      opacity: 0.8

      &:hover
        opacity: 1

    .preview, .preview-display
      color: $white
      font-size: 5em
      flex: 1 1 30%

    .preview
      @media (max-height: $max-height-thumbnail)
        display: none
      @media (max-width: $max-width-thumbnail)
        display: none

    .preview-inner, .preview-inner-display
      align-items: center
      justify-content: center
      display: flex
      background-color: rgba(64, 64, 64, 0.3)
      height: 100%
      width: 100%

    .form-wrapper
      background-image: linear-gradient(90deg, #0F9FEE, #8A5BE4)
      color: $white
      flex: 1 1 70%

      .form-header
        margin-left: 30px
        margin-right: auto
        margin-top: 8px
        @media (max-width: 750px)
          margin-top: 40px

        margin-bottom: 8px
        display: flex
        max-width: 900px
        font-size: 18px
        font-weight: 300
        @media screen and (min-width: 767px)
          justify-content: left
          margin-left: auto
          margin-top: 15px
          margin-bottom: 15px
          padding-left: 30px
          padding-right: 30px
        .togo-icon-gotomeeting
          font-size: 30px
          margin-right: 10px
      input:focus
        background-color: transparent
        color: $white
      input
        font-family: 'Lato', sans-serif

    .registration-disclaimer
      padding: 12px
      font-size: 11px
      color: $white
      @media (min-width: $min-embed-width+1)
        order: 2

</style>
